import {
	AutoCustomCompleteField,
	Checkbox,
	CustomDateControl,
	FormField,
	FormGrid,
	FormShell,
	MessageAlert,
	RBButton,
	RbToolbarActionButton,
	Tooltip,
} from "@scmlatam/web-ui";
import { Icon } from "@mui/material";
import dayjs from "dayjs";
import type { AddTagProps } from "../types/component";

const INDEFINITE_DATE = dayjs("3000-01-01");

const formatBrowserDate = (value?: string) => {
	if (!value) return "";

	const [year, month, day] = value.split("T")[0].split("-").map(Number);
	if (!year || !month || !day) return value;
	if (year === 3000) return "Indefinido";

	return new Intl.DateTimeFormat(navigator.language).format(
		new Date(year, month - 1, day),
	);
};

const AddTag = ({ formProps }: AddTagProps) => {
	const getTabStyle = (active: boolean) => ({
		backgroundColor: active ? "#190007" : "#E8E6E3",
		color: active ? "#FFFFFF" : "#190007",
		border: "none",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		height: "40px",
		padding: "10px 20px",
		whiteSpace: "nowrap",
	});

	const endDateError =
		formProps.validationErrors.endDate.error || formProps.hasInvalidDateRange;
	const endDateHelperText = formProps.hasInvalidDateRange
		? formProps.t("assign_tag.validation.date_range")
		: formProps.validationErrors.endDate.error
			? formProps.validationErrors.endDate.message
			: "";
	const isEndDateIndefinite = formProps.endDate?.isSame(INDEFINITE_DATE, "day") ?? false;
	const handleIndefiniteEndDateChange = (indefinite: boolean) => {
		formProps.setEndDate(
			indefinite
				? INDEFINITE_DATE
				: formProps.startDate?.add(30, "day") ?? null,
		);
	};

	return (
		<FormShell
			open={formProps.openForm}
			title={formProps.title}
			size="m"
			onClose={formProps.handleCancel}
			loading={formProps.loadingTags}
			alert={
				formProps.validationMessage ? (
					<MessageAlert
						type="warning"
						title={formProps.validationMessage}
						message=""
						className="rb-message-alert--form"
						actions={[
							{
								label: "OK",
								onClick: () => formProps.setValidationMessage(""),
							},
						]}
					/>
				) : formProps.showMessage ? (
					<MessageAlert
						type="warning"
						title={formProps.t("assign_tag.alerts.unsaved_changes.title")}
						message={formProps.t("assign_tag.alerts.unsaved_changes.message")}
						className="rb-message-alert--form"
						actions={[
							{
								label: formProps.t(
									"assign_tag.alerts.unsaved_changes.action_button",
								),
								onClick: () => formProps.setShowMessage(false),
							},
							{
								label: formProps.t(
									"assign_tag.alerts.unsaved_changes.cancel_button",
								),
								onClick: formProps.closeForm,
							},
						]}
					/>
				) : undefined
			}
			footer={
				<div className="flex flex-wrap items-center justify-end gap-3 p-3">
					<RBButton
						variant="secondary"
						size="m"
						disabled={formProps.loading}
						onClick={formProps.handleCancel}
					>
						{formProps.t("assign_tag.buttons.cancel")}
					</RBButton>
					<RBButton
						size="m"
						loading={formProps.loading}
						disabled={!formProps.canSubmit}
						onClick={formProps.submit}
					>
						{formProps.t("assign_tag.buttons.assign")}
					</RBButton>
				</div>
			}
		>
			<div className="flex flex-col gap-6">
				<section className="flex flex-col gap-3 px-1 py-1">
					<span className="text-base font-medium text-[#5D5854]">
						{formProps.t("assign_tag.selected_workers", {
							count: formProps.rowsSelected.length,
						})}
					</span>
					<div className="flex flex-wrap gap-2">
						{formProps.rowsSelected.map((row) => (
							<span
								key={row.id}
								className="inline-flex max-w-full truncate items-center rounded-full bg-[#E8E6E3] px-3 py-1 text-sm font-medium text-[#5D5854]"
								style={{ backgroundColor: "#E8E6E3" }}
								title={`${row.person_number} - ${row.fullname}`}
							>
								{row.person_number} - {row.fullname}
							</span>
						))}
					</div>
				</section>

				<div className="flex flex-wrap gap-4">
					<button
						type="button"
						className="rounded-full text-base font-semibold transition-colors"
						style={getTabStyle(formProps.tab === "new")}
						onClick={() => formProps.setTab("new")}
					>
						{formProps.t("assign_tag.tabs.new")}
					</button>
					{formProps.rowsSelected.length === 1 && (
						<button
							type="button"
							className="rounded-full text-base font-semibold transition-colors"
							style={getTabStyle(formProps.tab === "existing")}
							onClick={() => formProps.setTab("existing")}
						>
							{formProps.t("assign_tag.tabs.existing")}
						</button>
					)}
				</div>

				{formProps.tab === "new" ? (
					<FormGrid>
						<FormField span={12}>
							<AutoCustomCompleteField
								label={formProps.t("assign_tag.fields.tag")}
								placeholder={formProps.t("assign_tag.placeholder.select")}
								values={formProps.tags}
								value={formProps.selectedTags}
								setValue={formProps.setSelectedTags}
								disabled={formProps.loadingTags || formProps.loading}
								white
								error={formProps.validationErrors.tags.error}
								helperText={
									formProps.validationErrors.tags.error
										? formProps.validationErrors.tags.message
										: ""
								}
								search={{
									value: formProps.searchTag,
									onChange: formProps.setSearchTag,
									placeholder: formProps.t("assign_tag.placeholder.select"),
									mode: "local",
									emptyText: formProps.t("assign_tag.placeholder.empty"),
									onCreateOption: formProps.canCreateTag
										? () => formProps.createAndSelectTag()
										: undefined,
									...(formProps.canCreateTag
										? {
											createOptionLabel: (value: string) =>
												formProps.t("assign_tag.buttons.create_tag", { tag: value }),
										}
										: {}),
								}}
							/>
						</FormField>

						<FormField span={6}>
							<CustomDateControl
								label={formProps.t("assign_tag.fields.start_date")}
								initialValue={null}
								value={formProps.startDate}
								setValue={formProps.setStartDate}
								disabled={formProps.loading}
								white
								fullWidth
								placeholder={formProps.t("assign_tag.placeholder.date")}
								error={formProps.validationErrors.startDate.error}
								helperText={
									formProps.validationErrors.startDate.error
										? formProps.validationErrors.startDate.message
										: ""
								}
							/>
						</FormField>

						<FormField span={6}>
							<CustomDateControl
								label={formProps.t("assign_tag.fields.end_date")}
								value={formProps.endDate}
								setValue={formProps.setEndDate}
								indefinite={isEndDateIndefinite}
								onIndefiniteChange={handleIndefiniteEndDateChange}
								indefiniteProps={{
									indefiniteLabel: formProps.t("assign_tag.fields.indefinite"),
									showIndefiniteCheckbox: true,
								}}
								disabled={formProps.loading}
								white
								fullWidth
								placeholder={formProps.t("assign_tag.placeholder.date")}
								error={endDateError}
								helperText={endDateHelperText}
							/>
						</FormField>

						<FormField span={12}>
							<Checkbox
								checked={formProps.overwrite}
								disabled={formProps.loading}
								label={formProps.t("assign_tag.fields.overwrite")}
								onChange={(event) => formProps.setOverwrite(event.target.checked)}
							/>
						</FormField>
					</FormGrid>
				) : (
					<section className="flex flex-col gap-5 px-2 py-1">
						{formProps.loadingRanges ? (
							<p className="py-4 text-center text-sm font-medium text-[#5D5854]">
								{formProps.t("assign_tag.existing.loading")}
							</p>
						) : formProps.draftExistingRanges.length === 0 ? (
							<p className="py-4 text-center text-sm font-medium text-[#5D5854]">
								{formProps.t("assign_tag.existing.empty")}
							</p>
						) : (
							formProps.draftExistingRanges.map((range) => {
								const displayValues = formProps.getRangeDisplayValues(range);

								if (formProps.isRangeEditing(range)) return (
									<article
										key={range.employee_r_tag_id ?? `${range.tag_id}-${range.start_date}`}
										className="rounded-lg border border-[#CECDCA] px-3 py-4"
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: "16px",
												width: "100%",
											}}
										>
											<div style={{ flex: "1 1 auto", minWidth: 0 }}>
												<AutoCustomCompleteField
													label={formProps.t("assign_tag.fields.tag")}
													placeholder={formProps.t("assign_tag.placeholder.select")}
													values={formProps.tags}
													value={formProps.selectedTags}
													setValue={formProps.setSelectedTags}
													disabled={formProps.loadingTags || formProps.loading}
													white
													error={formProps.validationErrors.tags.error}
													helperText={
														formProps.validationErrors.tags.error
															? formProps.validationErrors.tags.message
															: ""
													}
													search={{
														value: formProps.searchTag,
														onChange: formProps.setSearchTag,
														placeholder: formProps.t("assign_tag.placeholder.select"),
														mode: "local",
														emptyText: formProps.t("assign_tag.placeholder.empty"),
														onCreateOption: formProps.canCreateTag
															? () => formProps.createAndSelectTag()
															: undefined,
														...(formProps.canCreateTag
															? {
																createOptionLabel: (value: string) =>
																	formProps.t("assign_tag.buttons.create_tag", { tag: value }),
															}
															: {}),
													}}
												/>
											</div>
											<div
												style={{
													display: "flex",
													flex: "0 0 84px",
													justifyContent: "flex-end",
													gap: "4px",
												}}
											>
												<Tooltip content={formProps.t("assign_tag.existing.tooltips.save_edit")}>
													<RbToolbarActionButton
														variant="icon"
														disabled={formProps.loading}
														onClick={formProps.saveRangeEdit}
														icon={
															<Icon baseClassName="material-symbols-rounded" sx={{ color: "#26000B", fontSize: "22px" }}>
																check
															</Icon>
														}
													/>
												</Tooltip>
												<Tooltip content={formProps.t("assign_tag.existing.tooltips.delete")}>
												<RbToolbarActionButton
													variant="icon"
													disabled={formProps.loading}
													onClick={() => formProps.deleteRange(range)}
													icon={
														<Icon
															baseClassName="material-symbols-rounded"
															sx={{ color: "#26000B", fontSize: "22px" }}
														>
															delete
														</Icon>
													}
												/>
												</Tooltip>
											</div>
										</div>
										<div className="mt-5 grid grid-cols-2 gap-5">
											<CustomDateControl
												label={formProps.t("assign_tag.fields.start_date")}
												initialValue={null}
												value={formProps.startDate}
												setValue={formProps.setStartDate}
												disabled={formProps.loading}
												white
												fullWidth
												placeholder={formProps.t("assign_tag.placeholder.date")}
												error={formProps.validationErrors.startDate.error}
												helperText={
													formProps.validationErrors.startDate.error
														? formProps.validationErrors.startDate.message
														: ""
												}
											/>
											<CustomDateControl
												label={formProps.t("assign_tag.fields.end_date")}
												value={formProps.endDate}
												setValue={formProps.setEndDate}
												indefinite={isEndDateIndefinite}
												onIndefiniteChange={handleIndefiniteEndDateChange}
												indefiniteProps={{
													indefiniteLabel: formProps.t("assign_tag.fields.indefinite"),
													showIndefiniteCheckbox: true,
												}}
												disabled={formProps.loading}
												white
												fullWidth
												placeholder={formProps.t("assign_tag.placeholder.date")}
												error={endDateError}
												helperText={endDateHelperText}
											/>
										</div>
									</article>
								);

								return (
									<article
										key={range.employee_r_tag_id ?? `${range.tag_id}-${range.start_date}`}
										className="flex items-center justify-between gap-6 px-1"
									>
										<div className="flex min-w-0 flex-col gap-2">
											<span className="text-base font-semibold text-[#26000B]">
												{formProps.getTagName(displayValues.tagId)}
											</span>
											<span className={`text-sm font-medium ${formProps.isRangeDeleted(range) ? "text-[#A32626]" : "text-[#5D5854]"}`}>
												{formProps.isRangeDeleted(range)
													? formProps.t("assign_tag.existing.deleted")
													: `${formatBrowserDate(displayValues.startDate)} ${formatBrowserDate(displayValues.endDate)}`}
											</span>
										</div>
										<div className="ml-auto flex shrink-0 items-center gap-3">
											{formProps.isRangeDeleted(range) ? (
												<Tooltip content={formProps.t("assign_tag.existing.tooltips.restore")}>
													<RbToolbarActionButton
														variant="icon"
														disabled={formProps.loading}
														onClick={() => formProps.restoreRange(range)}
														icon={<Icon baseClassName="material-symbols-rounded" sx={{ color: "#26000B", fontSize: "22px" }}>undo</Icon>}
													/>
												</Tooltip>
											) : (
												<>
													<Tooltip content={formProps.t(formProps.hasRangeChanges(range) ? "assign_tag.existing.tooltips.undo_edit" : "assign_tag.existing.tooltips.edit")}>
											<RbToolbarActionButton
												variant="icon"
												disabled={formProps.loading}
														onClick={() => formProps.hasRangeChanges(range) ? formProps.undoRangeEdit(range) : formProps.editRange(range)}
												icon={
													<Icon
														baseClassName="material-symbols-rounded"
														sx={{ color: "#26000B", fontSize: "22px" }}
													>
																{formProps.hasRangeChanges(range) ? "undo" : "edit_square"}
													</Icon>
												}
											/>
												</Tooltip>
												<Tooltip content={formProps.t("assign_tag.existing.tooltips.delete")}>
											<RbToolbarActionButton
												variant="icon"
												disabled={formProps.loading}
												onClick={() => formProps.deleteRange(range)}
												icon={
													<Icon
														baseClassName="material-symbols-rounded"
														sx={{ color: "#26000B", fontSize: "22px" }}
													>
														delete
													</Icon>
												}
											/>
												</Tooltip>
											</>
										)}
										</div>
									</article>
								);
							})
						)}
					</section>
				)}
			</div>
		</FormShell>
	);
};

export { AddTag };
